import React, { useState, useEffect, useRef } from 'react';
import { Download, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getFirebaseAuth } from '../../services/firebase';
import { signOut } from 'firebase/auth';

export function LegalDisclaimerModal() {
    const [isOpen, setIsOpen] = useState(false);
    const [hasMounted, setHasMounted] = useState(false);
    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [check1, setCheck1] = useState(false);
    const [check2, setCheck2] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setHasMounted(true);
        const hasAccepted = localStorage.getItem('legal_disclaimer_accepted');
        if (!hasAccepted) {
            setIsOpen(true);
        }
    }, []);

    useEffect(() => {
        if (isOpen && scrollContainerRef.current) {
            const el = scrollContainerRef.current;
            if (el.scrollHeight <= el.clientHeight + 10) {
                setIsScrolledToBottom(true);
                setScrollProgress(100);
            }
        }
    }, [isOpen]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const progress = Math.min(100, Math.max(0, (scrollTop / (scrollHeight - clientHeight)) * 100));
        setScrollProgress(progress);
        
        const bottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 20;
        if (bottom) setIsScrolledToBottom(true);
    };

    const handleDecline = async () => {
        try {
            const auth = getFirebaseAuth();
            await signOut(auth);
            setIsOpen(false);
        } catch (error) {
            console.error("Failed to sign out on decline", error);
        }
    };

    const handleAccept = () => {
        if (!isScrolledToBottom || !check1 || !check2) return;
        localStorage.setItem('legal_disclaimer_accepted', 'true');
        setIsOpen(false);
    };

    if (!hasMounted || !isOpen) return null;

    return (
        <div className="fixed inset-0 z-[999999] flex items-center justify-center p-0 sm:p-4 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm">
            {/* Standard, elegant modal width (max-w-4xl) */}
            <div className="relative w-full max-w-4xl h-full sm:h-[98vh] bg-white dark:bg-[#0f172a] rounded-none sm:rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] overflow-hidden flex flex-col border-0 sm:border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in-95 duration-200">
                
                {/* Minimal Header */}
                <div className="relative shrink-0 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0f172a]">
                    <div className="px-6 sm:px-8 py-5 flex items-center justify-between">
                        <div>
                            <h2 className="text-[18px] font-semibold text-slate-900 dark:text-slate-100 tracking-tight">
                                Legal Notice & Risk Disclaimer
                            </h2>
                            <p className="text-[13px] text-slate-500 dark:text-slate-400 mt-0.5">
                                Version 1.0.0 • Please read carefully before proceeding
                            </p>
                        </div>
                        <button 
                            onClick={() => window.print()}
                            className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-md text-[12px] font-medium transition-colors"
                        >
                            <Download className="w-3.5 h-3.5" /> PDF
                        </button>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="absolute bottom-0 left-0 h-[2px] bg-slate-200 dark:bg-slate-800 w-full" />
                    <div className="absolute bottom-0 left-0 h-[2px] bg-indigo-500 transition-all duration-150 ease-out" style={{ width: `${scrollProgress}%` }} />
                </div>

                {/* Content: Standard font size (14px), line-height (1.6), normal weight */}
                <div 
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto custom-scrollbar p-6 sm:p-8 bg-slate-50/50 dark:bg-[#0b1120]"
                >
                    <div className="space-y-6 text-[14px] text-slate-600 dark:text-slate-300 leading-[1.6]">
                        
                        <p className="p-4 bg-rose-50 dark:bg-rose-900/20 text-rose-800 dark:text-rose-200 rounded-lg border border-rose-100 dark:border-rose-900/50">
                            <strong>Strict Compliance Enforced:</strong> By accessing, viewing, or using the Options Oracle platform ("Software", "Platform", or "Service"), you are entering into a legally binding agreement. You are agreeing to be bound by the following comprehensive Terms of Service, Risk Disclosure, and Liability Waiver. If you do not agree to these terms in their entirety, you must immediately close this application and cease all use.
                        </p>

                        <div className="space-y-5">
                            <div>
                                <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">1. No Financial Advice & Non-Registration</h3>
                                <p>This Software is strictly an analytical tool providing structural market data, quantitative modeling, and mathematical computations for educational, research, and informational purposes only. The developers, owners, affiliates, and contributors of Options Oracle are NOT registered under the SEBI (Investment Advisers) Regulations, 2013, the SEBI (Research Analysts) Regulations, 2014, or with any other regulatory body. No data, metric, algorithmic projection, layout, or analysis provided herein constitutes, nor should it ever be construed as, a recommendation or solicitation to buy, sell, or hold any security, derivative, or financial instrument.</p>
                            </div>

                            <div>
                                <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">2. Extreme Risk Warning & Capital Loss</h3>
                                <p>Trading in financial markets—specifically derivatives, options contracts, and margin trading—is highly speculative and carries a profound, substantial risk of total capital loss. Options trading is inherently dangerous and is not suitable for all investors. Market volatility, liquidity crunches, and gap openings can lead to losses that significantly exceed your initial investments. Historical market behaviors, backtested results, and algorithmic models are definitively not indicative of future results. You must be prepared to lose your entire trading capital.</p>
                            </div>

                            <div>
                                <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">3. "As-Is" Software & Data Inaccuracies (No Warranty)</h3>
                                <p>The Software and its data feeds are provided on an "AS-IS" and "AS-AVAILABLE" basis, without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement. Market data is sourced from third-party APIs (e.g., Upstox, Google Sheets) and may experience severe latency, complete interruptions, anomalies, or gross inaccuracies. The developers make absolutely no guarantee regarding the real-time accuracy, completeness, timeliness, or reliability of any data presented. You are solely responsible for independently verifying all data with your official broker before executing any trades.</p>
                            </div>

                            <div>
                                <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">4. Absolute Liability Waiver & Hold Harmless</h3>
                                <p>By using this Platform, you unconditionally acknowledge and agree that all capital allocations, market executions, and trading strategies implemented based directly or indirectly on the data from this Software are executed 100% at your absolute discretion and personal risk.</p>
                                <p className="mt-2 text-[12px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                    Under no circumstances shall the developers, owners, contributors, or affiliates be held liable for any direct, indirect, incidental, punitive, special, or consequential damages (including without limitation, lost profits, lost trading capital, or business interruption) arising out of the use, inability to use, or system failures of this software, even if advised of the possibility of such damages.
                                </p>
                                <p className="mt-2">You expressly agree to indemnify, defend, and hold harmless the developers of Options Oracle from and against any and all claims, liabilities, damages, losses, or expenses (including legal fees) arising out of your use of the Platform.</p>
                            </div>

                            <div>
                                <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">5. Jurisdictional & Compliance Obligations</h3>
                                <p>This agreement constitutes a valid and binding electronic contract under the Information Technology Act, 2000. You are solely responsible for ensuring that your use of this Software complies with the Securities and Exchange Board of India (SEBI) Act, 1992, and all applicable local, national, and international laws governing securities trading.</p>
                            </div>

                            <div>
                                <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">6. Intellectual Property & Anti-Scraping</h3>
                                <p>All algorithms, UI designs, code, and analytical methodologies contained within Options Oracle are the exclusive intellectual property of the developers. You are strictly prohibited from reverse-engineering, copying, redistributing, or using automated systems (scraping/bots) to extract data from this Platform.</p>
                            </div>

                            <div>
                                <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">7. Right to Terminate Access</h3>
                                <p>The administrators reserve the absolute right to suspend, revoke, or terminate your access to the Platform at any time, for any reason, without prior notice or liability. Continued use of the Platform signifies ongoing acceptance of these terms, which may be updated at the developers' discretion.</p>
                            </div>

                            <div>
                                <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">8. Data Privacy & Third-Party Services</h3>
                                <p>This Platform relies on third-party APIs (such as Upstox and Google services). By using this platform, you consent to the routing of necessary diagnostic and usage data. We are not responsible for the privacy practices, uptime, or data handling of these third-party vendors.</p>
                            </div>

                            <div>
                                <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">9. Severability & Amendments</h3>
                                <p>If any provision of this agreement is found to be unenforceable or invalid by a court of competent jurisdiction, that provision shall be limited or eliminated to the minimum extent necessary so that this agreement shall otherwise remain in full force and effect. The developers reserve the right to modify these terms at any time; continued use constitutes acceptance of the amended terms.</p>
                            </div>

                            <div>
                                <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">10. Capacity to Contract & Dispute Resolution</h3>
                                <p>By accepting these terms, you warrant that you are of legal age (18+ years) and possess the legal capacity to enter into a binding contract (under the Indian Contract Act, 1872). You explicitly agree to waive any right to participate in a class-action lawsuit. Any disputes arising from the use of this Platform shall be settled exclusively through binding arbitration in the jurisdiction determined by the developers, in accordance with the Arbitration and Conciliation Act, 1996.</p>
                            </div>

                            <div>
                                <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">11. Force Majeure</h3>
                                <p>The developers shall not be held liable for any failure or delay in performance resulting from causes beyond their reasonable control, including but not limited to acts of God, natural disasters, internet service provider failures, denial-of-service attacks, API outages, or governmental actions.</p>
                            </div>

                            <div>
                                <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">12. Acceptable Use Policy</h3>
                                <p>You agree not to use the Platform for any unlawful purpose, including but not limited to market manipulation, money laundering, fraud, or transmitting malware. Violation of this policy will result in immediate termination and potential reporting to law enforcement authorities under applicable acts such as the Prevention of Money Laundering Act, 2002 (PMLA).</p>
                            </div>
                        </div>

                        {/* Checkboxes Section */}
                        <div className="pt-6 mt-6 border-t border-slate-200 dark:border-slate-800 space-y-3">
                            <label className={cn(
                                "flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-all",
                                check1 
                                    ? "bg-slate-50 dark:bg-slate-800/50 border-slate-300 dark:border-slate-600" 
                                    : "bg-white dark:bg-[#0b1120] border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700",
                                !isScrolledToBottom && "opacity-50 pointer-events-none"
                            )}>
                                <div className="pt-0.5">
                                    <input 
                                        type="checkbox" 
                                        checked={check1} 
                                        onChange={(e) => setCheck1(e.target.checked)}
                                        disabled={!isScrolledToBottom}
                                        className="w-[18px] h-[18px] rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 dark:border-slate-700 dark:bg-slate-900 dark:checked:bg-indigo-500 cursor-pointer"
                                    />
                                </div>
                                <div className="text-[14px] text-slate-800 dark:text-slate-200 leading-snug">
                                    I acknowledge that options trading carries a high risk of total capital loss, and I assume 100% responsibility.
                                </div>
                            </label>

                            <label className={cn(
                                "flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-all",
                                check2 
                                    ? "bg-slate-50 dark:bg-slate-800/50 border-slate-300 dark:border-slate-600" 
                                    : "bg-white dark:bg-[#0b1120] border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700",
                                !isScrolledToBottom && "opacity-50 pointer-events-none"
                            )}>
                                <div className="pt-0.5">
                                    <input 
                                        type="checkbox" 
                                        checked={check2} 
                                        onChange={(e) => setCheck2(e.target.checked)}
                                        disabled={!isScrolledToBottom}
                                        className="w-[18px] h-[18px] rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 dark:border-slate-700 dark:bg-slate-900 dark:checked:bg-indigo-500 cursor-pointer"
                                    />
                                </div>
                                <div className="text-[14px] text-slate-800 dark:text-slate-200 leading-snug">
                                    I have read, understood, and agree to the Master Terms of Service and Liability Disclosures.
                                </div>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Minimal Footer */}
                <div className="shrink-0 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0f172a] px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-[12px] font-bold tracking-widest uppercase">
                        {isScrolledToBottom ? (
                            <span className="text-emerald-600 dark:text-emerald-500 flex items-center gap-1.5"><Check className="w-4 h-4" /> Fully Reviewed</span>
                        ) : (
                            <span className="text-slate-500 dark:text-slate-400">Review Pending</span>
                        )}
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <button
                            onClick={handleDecline}
                            className="w-full sm:w-auto px-6 py-2.5 rounded-lg text-[13px] font-semibold border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                        >
                            DECLINE
                        </button>
                        <button
                            onClick={handleAccept}
                            disabled={!isScrolledToBottom || !check1 || !check2}
                            className={cn(
                                "w-full sm:w-auto px-6 py-2.5 rounded-lg text-[13px] font-semibold transition-all",
                                isScrolledToBottom && check1 && check2
                                    ? "bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                                    : "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed"
                            )}
                        >
                            I ACCEPT
                        </button>
                    </div>
                </div>
                
            </div>
        </div>
    );
}
